import { apm } from '@elastic/apm-rum';
import { classifyFailure, toErrorCodeLabel } from './journey-outcome';
import type { JourneyOutcome } from './journey-outcome';

/**
 * Outcome normally not used on the RUM side
 * Allow it to be set for custom transactions
 */
declare module '@elastic/apm-rum' {
	interface Transaction {
		outcome: string;
	}
}
/**
 * You must call transaction.end()
 */
function rumTransaction(name: string, type = 'user-interaction') {
	return apm.startTransaction(name, type);
}

type JourneyLabels = Record<string, string | number | boolean>;

/**
 * Starts a journey transaction (one guest action, e.g. "Account Creation")
 * that is traced through to the backend. Call succeed() or fail() exactly once;
 * later calls are ignored.
 *
 * rumTransaction() starts an unmanaged transaction, which never becomes the
 * agent's current transaction: requests made while it is open are recorded on
 * the auto "Click - button" transaction and carry that transaction's trace id,
 * so the named transaction can't be matched to its backend trace. This one is
 * managed, so it replaces the click transaction and its requests are recorded
 * as its spans. GEM-208, GEM-206.
 *
 * The blocking span covers the whole action. It keeps the transaction open
 * until the outcome is recorded (a managed transaction otherwise ends as soon
 * as its last request finishes) and keeps it from being dropped when it fails
 * before any request is made (the agent drops managed transactions with no
 * spans).
 */
function rumJourneyTransaction(name: string, labels: JourneyLabels = {}) {
	const transaction = apm.startTransaction(name, 'user-interaction', { managed: true });
	const span = transaction?.startSpan(name, 'app', { blocking: true });
	transaction?.addLabels(labels);

	let finished = false;
	const finish = (outcome: JourneyOutcome, finishLabels: JourneyLabels) => {
		if (finished || !transaction) return;
		finished = true;
		transaction.addLabels({ fs_outcome: outcome, ...finishLabels });
		transaction.outcome = outcome === 'technical_failure' ? 'failure' : 'success';
		span?.end();
		transaction.end();
	};

	return {
		succeed: (finishLabels: JourneyLabels = {}) => finish('success', finishLabels),
		fail: (
			outcome: Exclude<JourneyOutcome, 'success'>,
			errorCode?: string | null,
			finishLabels: JourneyLabels = {}
		) => finish(outcome, { fs_error_code: toErrorCodeLabel(errorCode), ...finishLabels }),
	};
}

type RumJourneyTransaction = ReturnType<typeof rumJourneyTransaction>;

/**
 * Sends a one-off event as a transaction that ends as soon as it starts, so it's queued even if
 * the guest leaves the page straight after. Never throws.
 */
function rumEvent(name: string, labels: JourneyLabels = {}) {
	try {
		const start = performance.now();
		const transaction = apm.startTransaction(name, 'user-interaction', { startTime: start });
		transaction?.addLabels(labels);
		// The agent truncates durations to whole ms and drops any transaction that comes out at 0.
		transaction?.end(start + 1);
	} catch {
		// telemetry must never interrupt the guest's action
	}
}

type AddToCart = {
	cartType: 'stay' | 'product' | 'journey' | 'amenity';
	hotelCode?: string | null;
	forceAddToCart?: boolean;
	/** Stays and journeys. */
	roomCode?: string | null;
	/** Products and amenities. */
	productName?: string | null;
};

// Pressed and succeeded share labels so the two counts can be compared group by group.
const addToCartLabels = ({
	cartType,
	hotelCode,
	forceAddToCart = false,
	roomCode,
	productName,
	cartTraceId,
}: AddToCart & { cartTraceId: string }) => ({
	cartType,
	hotelCode: hotelCode || 'unknown',
	forceAddToCart,
	...(cartType === 'stay' || cartType === 'journey'
		? { roomCode: roomCode || 'unknown' }
		: { productName: productName || 'unknown' }),
	cart_trace_id: cartTraceId,
});

const newCartTraceId = () =>
	globalThis.crypto?.randomUUID?.() ??
	`${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;

// On globalThis, not module scope, so a press in one microapp bundle reaches the store in another.
const pressSlot = globalThis as typeof globalThis & {
	__fsPendingCartTrace?: { id: string; at: number };
};
const PRESS_TO_ADD_MAX_MS = 30_000;

/** Call from the add-to-cart button's own click handler, ahead of any validation or request. */
function rumAddToCartPressed(addToCart: AddToCart) {
	const cartTraceId = newCartTraceId();
	pressSlot.__fsPendingCartTrace = { id: cartTraceId, at: Date.now() };
	rumEvent('add_to_cart_pressed', addToCartLabels({ ...addToCart, cartTraceId }));
}

/**
 * Call once as an add starts. Returns the cart_trace_id of the press that led to it, or a new one
 * if there wasn't one, to send with the tRetail request and the success event.
 */
function takeCartTraceId(): string {
	const press = pressSlot.__fsPendingCartTrace;
	pressSlot.__fsPendingCartTrace = undefined;
	return press && Date.now() - press.at < PRESS_TO_ADD_MAX_MS ? press.id : newCartTraceId();
}

/** Call once the added product is confirmed to be in the cart, with the labels of its press. */
function rumAddToCartSucceeded(addToCart: AddToCart & { cartTraceId: string }) {
	rumEvent('add_to_cart_succeeded', addToCartLabels(addToCart));
}

export {
	rumTransaction,
	rumJourneyTransaction,
	rumEvent,
	rumAddToCartPressed,
	rumAddToCartSucceeded,
	takeCartTraceId,
	classifyFailure,
	toErrorCodeLabel,
};
export type { JourneyOutcome, RumJourneyTransaction };
